import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Search as SearchIcon, Storage as StorageIcon, Cloud as CloudIcon, Refresh as RefreshIcon, Error as ErrorIcon, Warning as WarningIcon, CheckCircle } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { trackingService } from '../services/dashboard.service';

const GATEWAYS = [
  { value: 'correios', label: 'Correios' },
  { value: 'jadlog', label: 'Jadlog' },
  { value: 'loggi', label: 'Loggi' },
  { value: 'melhorenvio', label: 'Melhor Envio' },
  { value: 'totalexpress', label: 'Total Express' },
  { value: 'fmtransportes', label: 'FM Transportes' },
  { value: 'superfrete', label: 'SuperFrete' }
];

export default function TrackingPage() {
  const [urlParams] = useSearchParams();
  const [gateway, setGateway] = useState('');
  const [trackId, setTrackId] = useState('');
  const [searchParams, setSearchParams] = useState<{ gateway: string; trackId: string } | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [reactivateDialog, setReactivateDialog] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [validateDialog, setValidateDialog] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  // Processa parâmetros da URL ao carregar o componente
  useEffect(() => {
    const gatewayParam = urlParams.get('gateway');
    const trackIdParam = urlParams.get('trackId');

    if (gatewayParam && trackIdParam) {
      setGateway(gatewayParam);
      setTrackId(trackIdParam);
      // Automaticamente busca quando vem da URL
      setSearchParams({ gateway: gatewayParam, trackId: trackIdParam });
    }
  }, [urlParams]);

  const { data: trackingData, isLoading, error, refetch } = useQuery({
    queryKey: ['tracking-lookup', searchParams?.gateway, searchParams?.trackId],
    queryFn: () => trackingService.directLookup(searchParams!.gateway, searchParams!.trackId),
    enabled: !!searchParams,
    retry: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!gateway || !trackId.trim()) {
      toast.error('Selecione uma transportadora e digite o código de rastreamento');
      return;
    }

    setSearchParams({ gateway, trackId: trackId.trim() });
  };

  const handleReactivate = useCallback(async () => {
    if (!searchParams) return;

    setIsReactivating(true);
    try {
      const result = await trackingService.reactivateTracking(searchParams.gateway, searchParams.trackId);
      
      // Verifica se a resposta indica sucesso
      if (result && (result.success || result.data)) {
        toast.success('Rastreamento reativado com sucesso!');
        setReactivateDialog(false);
        // Aguarda um pouco para o DynamoDB atualizar e então busca novamente
        setTimeout(() => {
          refetch();
        }, 2000);
      } else {
        // Caso retorne mas sem indicação clara de sucesso
        toast.success('Rastreamento reativado!');
        setReactivateDialog(false);
        setTimeout(() => {
          refetch();
        }, 2000);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao reativar rastreamento';
      toast.error(errorMsg);
    } finally {
      setIsReactivating(false);
    }
  }, [searchParams, refetch]);

  const handleValidateCredentials = useCallback(async () => {
    if (!searchParams || !trackingData) return;

    setIsValidating(true);
    setValidationResult(null);

    try {
      const accountId = trackingData.api?.account_id || trackingData.dynamodb?.account_id;
      const clientId = trackingData.api?.client_id || trackingData.dynamodb?.client_id;

      if (!accountId || !clientId) {
        toast.error('Não foi possível obter informações da conta');
        return;
      }

      // Buscar configuração do gateway
      const config = await trackingService.getGatewayConfig(searchParams.gateway, accountId, clientId);

      if (!config.found) {
        setValidationResult({
          valid: false,
          message: config.message || 'Configuração não encontrada'
        });
        return;
      }

      // Validar credenciais
      const result = await trackingService.validateCredentials(
        searchParams.gateway,
        accountId,
        clientId,
        config.credentials,
        config.settings
      );

      setValidationResult(result);

      if (result.valid) {
        toast.success('Credenciais válidas!');
      } else {
        toast.error('Credenciais inválidas');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Erro ao validar credenciais';
      setValidationResult({
        valid: false,
        message: errorMsg,
        errorCode: error.response?.data?.errorCode
      });
      toast.error(errorMsg);
    } finally {
      setIsValidating(false);
    }
  }, [searchParams, trackingData]);

  // Determina se deve mostrar o botão de validar credenciais
  const shouldShowValidate = useCallback(() => {
    if (!trackingData) return false;

    // Mostra botão se houver erro de credenciais
    const hasCredentialError = (
      trackingData.api?.detail?.description?.toLowerCase().includes('credential') ||
      trackingData.api?.detail?.description?.toLowerCase().includes('token') ||
      trackingData.api?.track_status === 'notfound'
    );

    return hasCredentialError;
  }, [trackingData]);

  // Determina se deve mostrar o botão de reativar
  const shouldShowReactivate = useCallback(() => {
    if (!trackingData) return false;

    // Se está ativo, não mostra botão de reativar
    const isActive = (
      trackingData.dynamodb?.object_status === 'active' ||
      trackingData.api?.object_status === 'active'
    );

    if (isActive) return false;

    // Verifica se alguma das fontes tem status de erro ou inativo
    const dynamoHasError = trackingData.dynamodb && (
      trackingData.dynamodb.object_status === 'error' ||
      trackingData.dynamodb.object_status === 'inactive' ||
      trackingData.dynamodb.track_status === 'error' ||
      trackingData.dynamodb.track_status === 'notfound'
    );

    const apiHasError = trackingData.api && (
      trackingData.api.object_status === 'error' ||
      trackingData.api.object_status === 'inactive' ||
      trackingData.api.track_status === 'error' ||
      trackingData.api.track_status === 'notfound'
    );

    // Não mostra para status finais positivos
    const isFinalPositive = (
      trackingData.dynamodb?.track_status === 'delivered' ||
      trackingData.api?.track_status === 'delivered' ||
      trackingData.dynamodb?.track_status === 'returned' ||
      trackingData.api?.track_status === 'returned'
    );

    return (dynamoHasError || apiHasError) && !isFinalPositive;
  }, [trackingData]);

  const formatEventStatus = (status: string) => {
    const statusMap: Record<string, { label: string; color: any }> = {
      'posted': { label: 'Postado', color: 'info' },
      'intransit': { label: 'Em Trânsito', color: 'warning' },
      'delivering': { label: 'Saiu para Entrega', color: 'secondary' },
      'delivered': { label: 'Entregue', color: 'success' },
      'returned': { label: 'Devolvido', color: 'error' },
      'error': { label: 'Erro', color: 'error' }
    };

    return statusMap[status] || { label: status, color: 'default' };
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Consulta de Rastreamento
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="end">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Transportadora</InputLabel>
                <Select
                  value={gateway}
                  label="Transportadora"
                  onChange={(e) => setGateway(e.target.value)}
                >
                  {GATEWAYS.map((gw) => (
                    <MenuItem key={gw.value} value={gw.value}>
                      {gw.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código de Rastreamento"
                value={trackId}
                onChange={(e) => setTrackId(e.target.value)}
                placeholder="Ex: AM913671052BR"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                disabled={isLoading}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Erro ao consultar rastreamento: {error.message}
        </Alert>
      )}

      {trackingData && (
        <Box>
          <Paper sx={{ mb: 3, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab icon={<StorageIcon />} label="DynamoDB (Tempo Real)" />
                <Tab icon={<CloudIcon />} label="API/Elasticsearch" />
                <Tab label="Comparação" />
              </Tabs>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {shouldShowValidate() && (
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<WarningIcon />}
                    onClick={() => setValidateDialog(true)}
                    disabled={isValidating}
                  >
                    Validar Credenciais
                  </Button>
                )}
                {shouldShowReactivate() && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RefreshIcon />}
                    onClick={() => setReactivateDialog(true)}
                    disabled={isReactivating}
                  >
                    Reativar Rastreamento
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Seção de Detalhes/Informações - Sempre visível quando há detail em qualquer fonte */}
          {(trackingData?.api as any)?.detail && (
            <Alert
              severity={trackingData.api?.track_status === 'notfound' ? 'warning' :
                       (trackingData.api?.track_status === 'error' ? 'error' : 'info')}
              sx={{ mb: 3 }}
            >
              <Typography variant="h6" gutterBottom>
                {trackingData.api?.track_status === 'notfound' ? 'Objeto Não Encontrado' :
                 trackingData.api?.track_status === 'error' ? 'Erro no Rastreamento' : 'Informação do Rastreamento'}
              </Typography>
              {(trackingData.api as any).detail.description && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {trackingData.api?.track_status === 'notfound' ? 'Motivo:' :
                     trackingData.api?.track_status === 'error' ? 'Descrição do erro:' : 'Informação:'}
                  </Typography>
                  <Typography variant="body2">
                    {(trackingData.api as any).detail.description}
                  </Typography>
                </Box>
              )}
              {(trackingData.api as any).detail.location && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Localização:
                  </Typography>
                  <Typography variant="body2">
                    {(trackingData.api as any).detail.location}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Fonte: API/Elasticsearch
                </Typography>
              </Box>
            </Alert>
          )}

          {activeTab === 0 && (
            <>
              {/* Seção de Erro - Exibe quando há erro no tracking */}
              {(trackingData.dynamodb?.track_status === 'error' || trackingData.dynamodb?.object_status === 'error') && (
                <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
                  <Typography variant="h6" gutterBottom>
                    Detalhes do Erro
                  </Typography>
                  {trackingData.dynamodb?.last_event?.description && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Descrição do erro:
                      </Typography>
                      <Typography variant="body2">
                        {trackingData.dynamodb.last_event.description}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total de tentativas:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {trackingData.dynamodb.retry_count}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Status atual:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {trackingData.dynamodb.track_status}
                      </Typography>
                    </Box>
                    {trackingData.dynamodb.retry_count > 250 && (
                      <Box>
                        <Chip
                          icon={<WarningIcon />}
                          label="DESATIVADO"
                          color="error"
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                </Alert>
              )}

              <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      DynamoDB - Dados Atuais
                    </Typography>
                    {trackingData.dynamodb ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Status:</Typography>
                          <Chip
                            label={formatEventStatus(trackingData.dynamodb.track_status).label}
                            color={formatEventStatus(trackingData.dynamodb.track_status).color}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Status do Objeto:</Typography>
                          <Chip label={trackingData.dynamodb.object_status} />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Última Localização:</Typography>
                          <Typography variant="body1">{trackingData.dynamodb.last_location || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Tentativas:</Typography>
                          <Typography
                            variant="body1"
                            color={trackingData.dynamodb.retry_count > 100 ? 'error' : 'inherit'}
                            fontWeight={trackingData.dynamodb.retry_count > 100 ? 'bold' : 'normal'}
                          >
                            {trackingData.dynamodb.retry_count}
                            {trackingData.dynamodb.retry_count > 200 && (
                              <WarningIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle' }} color="error" />
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Criado em:</Typography>
                          <Typography variant="body2">
                            {new Date(trackingData.dynamodb.date_created).toLocaleString('pt-BR')}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Atualizado em:</Typography>
                          <Typography variant="body2">
                            {new Date(trackingData.dynamodb.date_updated).toLocaleString('pt-BR')}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Próxima Tentativa:</Typography>
                          <Typography variant="body2">
                            {new Date(trackingData.dynamodb.retry_at).toLocaleString('pt-BR')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Alert severity="warning">Nenhum dado encontrado no DynamoDB</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Último Evento (DynamoDB)
                    </Typography>
                    {trackingData.dynamodb?.last_event ? (
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">Data/Hora:</Typography>
                            <Typography variant="body1">
                              {new Date(trackingData.dynamodb.last_event.datetime).toLocaleString('pt-BR')}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">Status:</Typography>
                            <Chip
                              size="small"
                              label={formatEventStatus(trackingData.dynamodb.last_event.status).label}
                              color={formatEventStatus(trackingData.dynamodb.last_event.status).color}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">Descrição:</Typography>
                            <Typography variant="body1">{trackingData.dynamodb.last_event.description}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">Localização:</Typography>
                            <Typography variant="body1">{trackingData.dynamodb.last_event.location}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <Alert severity="info">Nenhum evento registrado</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            </>
          )}

          {activeTab === 1 && (
            <>
              {/* Seção de Erro para API - Exibe quando há erro no tracking mas sem detail */}
              {(trackingData.api?.track_status === 'error' || trackingData.api?.object_status === 'error') && !(trackingData.api as any)?.detail && (
                <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
                  <Typography variant="h6" gutterBottom>
                    Detalhes do Erro (API)
                  </Typography>
                  {trackingData.api?.detail?.description && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Descrição do erro:
                      </Typography>
                      <Typography variant="body2">
                        {(trackingData.api as any).detail.description}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total de tentativas:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {trackingData.api.retry_count}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Status atual:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {trackingData.api.track_status}
                      </Typography>
                    </Box>
                    {trackingData.api.retry_count > 250 && (
                      <Box>
                        <Chip
                          icon={<WarningIcon />}
                          label="DESATIVADO"
                          color="error"
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                </Alert>
              )}

              <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <CloudIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      API/ES - Dados Completos
                    </Typography>
                    {trackingData.api ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Status:</Typography>
                          <Chip
                            label={formatEventStatus(trackingData.api.track_status).label}
                            color={formatEventStatus(trackingData.api.track_status).color}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Status do Objeto:</Typography>
                          <Chip label={trackingData.api.object_status} />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Total de Eventos:</Typography>
                          <Typography variant="body1">{trackingData.api.events.length}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Tentativas:</Typography>
                          <Typography
                            variant="body1"
                            color={trackingData.api.retry_count > 100 ? 'error' : 'inherit'}
                            fontWeight={trackingData.api.retry_count > 100 ? 'bold' : 'normal'}
                          >
                            {trackingData.api.retry_count}
                            {trackingData.api.retry_count > 200 && (
                              <WarningIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle' }} color="error" />
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="textSecondary">Timestamp:</Typography>
                          <Typography variant="body2">
                            {new Date(trackingData.api.timestamp).toLocaleString('pt-BR')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Alert severity="warning">Nenhum dado encontrado na API/Elasticsearch</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Histórico Completo de Eventos (API/ES)
                    </Typography>
                    {trackingData.api?.events && trackingData.api.events.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data/Hora</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Descrição</TableCell>
                              <TableCell>Local</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {trackingData.api.events.map((event: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>
                                  {event.datetime ? new Date(event.datetime).toLocaleString('pt-BR') : '-'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={formatEventStatus(event.status || 'unknown').label}
                                    color={formatEventStatus(event.status || 'unknown').color}
                                  />
                                </TableCell>
                                <TableCell>{event.description || '-'}</TableCell>
                                <TableCell>{event.location || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Nenhum evento encontrado</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            </>
          )}

          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Comparação: DynamoDB vs API/Elasticsearch
                    </Typography>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Campo</TableCell>
                            <TableCell>DynamoDB (Tempo Real)</TableCell>
                            <TableCell>API/ES (Após Processamento)</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Track Status</strong></TableCell>
                            <TableCell>
                              {trackingData.dynamodb ? (
                                <Chip
                                  size="small"
                                  label={formatEventStatus(trackingData.dynamodb.track_status).label}
                                  color={formatEventStatus(trackingData.dynamodb.track_status).color}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {trackingData.api ? (
                                <Chip
                                  size="small"
                                  label={formatEventStatus(trackingData.api.track_status).label}
                                  color={formatEventStatus(trackingData.api.track_status).color}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {trackingData.dynamodb?.track_status === trackingData.api?.track_status ? (
                                <Chip size="small" label="Sincronizado" color="success" />
                              ) : (
                                <Chip size="small" label="Divergente" color="warning" />
                              )}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell><strong>Object Status</strong></TableCell>
                            <TableCell>{trackingData.dynamodb?.object_status || '-'}</TableCell>
                            <TableCell>{trackingData.api?.object_status || '-'}</TableCell>
                            <TableCell>
                              {trackingData.dynamodb?.object_status === trackingData.api?.object_status ? (
                                <Chip size="small" label="Sincronizado" color="success" />
                              ) : (
                                <Chip size="small" label="Divergente" color="warning" />
                              )}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell><strong>Retry Count</strong></TableCell>
                            <TableCell>{trackingData.dynamodb?.retry_count || 0}</TableCell>
                            <TableCell>{trackingData.api?.retry_count || 0}</TableCell>
                            <TableCell>
                              {trackingData.dynamodb?.retry_count === trackingData.api?.retry_count ? (
                                <Chip size="small" label="Sincronizado" color="success" />
                              ) : (
                                <Chip size="small" label="Divergente" color="warning" />
                              )}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell><strong>Última Atualização</strong></TableCell>
                            <TableCell>
                              {trackingData.dynamodb ? new Date(trackingData.dynamodb.date_updated).toLocaleString('pt-BR') : '-'}
                            </TableCell>
                            <TableCell>
                              {trackingData.api ? new Date(trackingData.api.timestamp).toLocaleString('pt-BR') : '-'}
                            </TableCell>
                            <TableCell>
                              {trackingData.dynamodb && trackingData.api ? (
                                new Date(trackingData.dynamodb.date_updated) > new Date(trackingData.api.timestamp) ? (
                                  <Chip size="small" label="DynamoDB mais recente" color="info" />
                                ) : (
                                  <Chip size="small" label="API/ES mais recente" color="secondary" />
                                )
                              ) : '-'}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell><strong>Account ID</strong></TableCell>
                            <TableCell>{trackingData.dynamodb?.account_id || '-'}</TableCell>
                            <TableCell>{trackingData.api?.account_id || '-'}</TableCell>
                            <TableCell>
                              {trackingData.dynamodb?.account_id === trackingData.api?.account_id ? (
                                <Chip size="small" label="Igual" color="success" />
                              ) : (
                                <Chip size="small" label="Diferente" color="error" />
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Modal de confirmação para reativar */}
      <Dialog
        open={reactivateDialog}
        onClose={() => setReactivateDialog(false)}
        aria-labelledby="reactivate-dialog-title"
      >
        <DialogTitle id="reactivate-dialog-title">
          Confirmar Reativação
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja reativar o rastreamento <strong>{trackingData?.trackId}</strong>?
            <br /><br />
            Esta ação vai:
            <ul>
              <li>Reativar o objeto no sistema de rastreamento</li>
              <li>Permitir novas tentativas de busca</li>
              <li>Atualizar o status no DynamoDB</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateDialog(false)} disabled={isReactivating}>
            Cancelar
          </Button>
          <Button
            onClick={handleReactivate}
            variant="contained"
            color="warning"
            disabled={isReactivating}
            startIcon={isReactivating ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {isReactivating ? 'Reativando...' : 'Confirmar Reativação'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para validar credenciais */}
      <Dialog
        open={validateDialog}
        onClose={() => !isValidating && setValidateDialog(false)}
        aria-labelledby="validate-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="validate-dialog-title">
          🔍 Validação de Credenciais - {searchParams?.gateway?.toUpperCase()}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Verificação completa das credenciais configuradas para a transportadora.
          </DialogContentText>

          {isValidating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Executando validação...</Typography>
            </Box>
          )}

          {validationResult && (
            <Box>
              <Alert 
                severity={validationResult.valid ? 'success' : 'error'} 
                sx={{ mb: 2 }}
                icon={validationResult.valid ? <CheckCircle /> : <ErrorIcon />}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {validationResult.message}
                </Typography>
                {validationResult.duration && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    ⏱️ Tempo de execução: {validationResult.duration}
                  </Typography>
                )}
              </Alert>

              {/* Logs detalhados */}
              {validationResult.logs && validationResult.logs.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    📋 Logs de Execução
                  </Typography>
                  {validationResult.logs.map((log: any, index: number) => (
                    <Card 
                      key={index} 
                      sx={{ 
                        mb: 2,
                        backgroundColor: log.step === 'ERROR' ? '#ffebee' : log.step === 3 ? '#e8f5e9' : '#f5f5f5',
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Chip 
                            label={log.step} 
                            size="small" 
                            color={log.step === 'ERROR' ? 'error' : 'primary'}
                          />
                          <Typography fontWeight="bold">{log.action}</Typography>
                          <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                            {new Date(log.timestamp).toLocaleTimeString('pt-BR')}
                          </Typography>
                        </Box>
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {log.url && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold">URL:</Typography>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{log.url}</Typography>
                            </Box>
                          )}
                          {log.payload && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Payload:</Typography>
                              <pre style={{ margin: 0, marginTop: 4, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4, overflow: 'auto' }}>
                                {JSON.stringify(log.payload, null, 2)}
                              </pre>
                            </Box>
                          )}
                          {log.status && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Status HTTP:</Typography>
                              <Chip label={log.status} size="small" color="success" sx={{ ml: 1 }} />
                            </Box>
                          )}
                          {log.responseData && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Resposta:</Typography>
                              <pre style={{ margin: 0, marginTop: 4, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4, overflow: 'auto' }}>
                                {JSON.stringify(log.responseData, null, 2)}
                              </pre>
                            </Box>
                          )}
                          {log.credentialKeys && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Campos de Credencial:</Typography>
                              <Typography variant="body2">{log.credentialKeys.join(', ')}</Typography>
                            </Box>
                          )}
                          {log.settingKeys && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Campos de Configuração:</Typography>
                              <Typography variant="body2">{log.settingKeys.join(', ')}</Typography>
                            </Box>
                          )}
                          {log.error && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="error">Erro:</Typography>
                              <Typography variant="body2" color="error">{log.error}</Typography>
                            </Box>
                          )}
                          {log.errorCode && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="error">Código do Erro:</Typography>
                              <Chip label={log.errorCode} size="small" color="error" sx={{ ml: 1 }} />
                            </Box>
                          )}
                          {log.errorResponse && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="error">Detalhes do Erro:</Typography>
                              <pre style={{ margin: 0, marginTop: 4, padding: 8, backgroundColor: '#ffebee', borderRadius: 4, overflow: 'auto' }}>
                                {JSON.stringify(log.errorResponse, null, 2)}
                              </pre>
                            </Box>
                          )}
                          {log.errorStack && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="error">Stack Trace:</Typography>
                              <pre style={{ margin: 0, marginTop: 4, padding: 8, backgroundColor: '#ffebee', borderRadius: 4, overflow: 'auto', fontSize: '0.75rem' }}>
                                {log.errorStack}
                              </pre>
                            </Box>
                          )}
                          {log.data && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Dados:</Typography>
                              <pre style={{ margin: 0, marginTop: 4, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4, overflow: 'auto' }}>
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* Detalhes adicionais */}
              {validationResult.details && Object.keys(validationResult.details).length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" gutterBottom>
                    📝 Detalhes da Validação
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {Object.entries(validationResult.details).map(([key, value]: [string, any]) => (
                          <TableRow key={key}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                              {key}
                            </TableCell>
                            <TableCell>
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateDialog(false)} disabled={isValidating}>
            Fechar
          </Button>
          {!validationResult && (
            <Button
              onClick={handleValidateCredentials}
              variant="contained"
              color="primary"
              disabled={isValidating}
              startIcon={isValidating ? <CircularProgress size={20} /> : <WarningIcon />}
            >
              {isValidating ? 'Validando...' : 'Iniciar Validação'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}